import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  Injector,
  OnDestroy,
  OnInit,
  QueryList,
  runInInjectionContext,
  signal,
  ViewChildren,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { ActivatedRoute } from '@angular/router';
import { SiaActionsContainerComponent } from '@sia/actions';
import {
  jsonDeepClone,
  retriveCurrentRouteData,
  SiaComponentConfigService,
  SiaLayoutService,
  SiaNewRouterService,
  SiaParamsAndPatternsService,
  SiaRouteData,
  SiaSessionParam,
  SiaSessionService,
  SiaSizeDetectorService,
  SiaTranslatePipe,
} from '@sia/shared';
import {
  SiaMultiGrid,
  SiaMultiGridConfiguration,
} from '../../interfaces/sia-multi-grid.configuration';
import { SiaMatGridComponent } from '../sia-mat-grid/sia-mat-grid.component';

@Component({
  selector: 'sia-multi-grid',
  standalone: true,
  imports: [
    SiaActionsContainerComponent,
    SiaMatGridComponent,
    SiaTranslatePipe,
    MatButtonModule,
    MatIconButton,
    CommonModule,
    MatIcon,
  ],
  templateUrl: './sia-multi-grid.component.html',
  styleUrl: './sia-multi-grid.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SiaMultiGridComponent implements OnInit, OnDestroy {
  // Services
  readonly #siaParamsAndPatternsService = inject(SiaParamsAndPatternsService);
  readonly #siaComponentConfigService = inject(SiaComponentConfigService);
  readonly #siaSizeDetector = inject(SiaSizeDetectorService);
  readonly #siaRouterService = inject(SiaNewRouterService);
  readonly #siaSessionService = inject(SiaSessionService);
  readonly #siaLayoutService = inject(SiaLayoutService);
  readonly #route = inject(ActivatedRoute);
  readonly #injector = inject(Injector);

  // Signals
  readonly siaMultiGrid = signal<SiaMultiGridConfiguration>({});
  readonly pageData = signal<SiaRouteData | undefined>(undefined);
  readonly multiGridId = signal<string>('');
  readonly expandedGridSize = signal<number>(0);
  readonly #gridFilterParameters = signal<any>({});

  // Computed signals
  readonly multiGridParameters = computed(() => this.#gridFilterParameters());
  readonly grids = computed(() => this.siaMultiGrid().grids ?? []);
  readonly hasGrids = computed(() => this.grids().length > 0);

  // Method to get the effective height for a grid
  getGridHeight(minHeight: string): number {
    const calculatedSize = this.expandedGridSize();
    const parsedMin =
      parseInt(minHeight.toString().replace(/px\s*$/i, ''), 10) || 0;
    return Math.max(calculatedSize, parsedMin);
  }

  // Convert observables to signals
  readonly #sizeChange = toSignal(this.#siaSizeDetector.onResize$);
  readonly #routeChange = toSignal(this.#siaRouterService.routeChanged$, {
    initialValue: null,
  });

  // ViewChildren to access grid components
  @ViewChildren(SiaMatGridComponent)
  gridComponents?: QueryList<SiaMatGridComponent>;

  // state
  #timeouts: any[] = [];

  constructor() {
    // Initialize multiGridId from route params
    this.multiGridId.set(this.#route.snapshot.params['multiGridId']);

    // Effect: Handle size changes
    effect(() => {
      const size = this.#sizeChange();
      if (size) {
        this.processGridsHeight();
      }
    });

    effect(() => {
      const routeChanged = this.#routeChange();
      if (routeChanged === null) {
        return;
      }
      this.handleRouteChange();
    });
  }

  private handleRouteChange(): void {
    let newPageData: any = null;
    runInInjectionContext(this.#injector, () => {
      newPageData = retriveCurrentRouteData() ?? {};
    });
    if (!newPageData?.guid || newPageData?.guid === this.pageData()?.guid) {
      return;
    }

    this.pageData.set(newPageData);
    this.multiGridId.set(this.#route.snapshot.params['multiGridId']);
    this.siaMultiGrid.set({});
    this.loadConfiguration();
  }

  rowClicked(rowData: any, siaMultiGrid?: SiaMultiGrid): void {
    if (!siaMultiGrid) {
      return;
    }

    // Check if clicking the same record or empty data to expand the grid
    if (
      JSON.stringify(rowData) === JSON.stringify(siaMultiGrid.selectedData) ||
      Object.keys(rowData).length === 0
    ) {
      this.expandGrid(siaMultiGrid);
      return;
    }
    this.setClickedRowData(siaMultiGrid, rowData);
    siaMultiGrid.selectedData = jsonDeepClone(rowData);
    this.processGridsHeight();

    // Update filter parameters signal
    this.#gridFilterParameters.update((params) => jsonDeepClone(params));

    this.#notifyGridChange();

    const t = setTimeout(() => {
      this.#siaParamsAndPatternsService.notifyHardParamsUpdated();
    });
    this.#timeouts.push(t);
  }

  private setClickedRowData(siaMultiGrid: SiaMultiGrid, rowData: any): void {
    const currentGridConfig = this.siaMultiGrid();
    const gridToCheck = currentGridConfig.grids?.filter((grid) =>
      grid.hideUntilValueFor?.some((item) =>
        siaMultiGrid.bindingProperties
          ?.map((x) => x.bindingName)
          .includes(item),
      ),
    );

    // Update filter parameters
    const updatedParams = { ...this.#gridFilterParameters() };
    siaMultiGrid.bindingProperties?.forEach((property) => {
      updatedParams[property.bindingName] = rowData[property.propertyName];
    });
    this.#gridFilterParameters.set(updatedParams);

    const sessionParams: SiaSessionParam[] = [];
    siaMultiGrid?.sessionProperties?.forEach((property) => {
      sessionParams.push({
        id: property.bindingName,
        value: rowData[property.propertyName],
      });
    });

    gridToCheck?.forEach((grid) => {
      let dontShowData = false;
      const currentParams = this.#gridFilterParameters();

      grid.hideUntilValueFor?.forEach((item) => {
        if (!currentParams[item]) {
          dontShowData = true;
        }
      });
      if (dontShowData) {
        grid.compact = true;
        grid.canLoadData = false;
        grid.selectedData = null;
      } else {
        grid.compact = false;
        grid.canLoadData = true;
      }

      // Clear the selection in the dependent grid's component
      if (this.gridComponents && grid.gridId) {
        const gridComponent = this.gridComponents.find(
          (gc) => gc.gridId() === grid.gridId,
        );
        if (gridComponent) {
          // Call toggleCompactMode with deselect: true and silent: true to clear selection
          gridComponent.toggleCompactMode({}, undefined, true, true);
        }
      }

      this.setClickedRowData(grid, {});
    });

    if (siaMultiGrid.allowCompact && Object.keys(rowData).length > 0) {
      siaMultiGrid.compact = true;
    }

    const currentPageData = this.pageData();
    this.#siaSessionService.updateSessionParams(
      currentPageData?.guid,
      sessionParams,
    );

    this.#notifyGridChange();
  }

  expandGrid(grid: SiaMultiGrid): void {
    grid.compact = false;
    grid.selectedData = null;
    grid.canLoadData = true;
    this.setClickedRowData(grid, {});
    this.processGridsHeight();

    // Find and call toggleCompactMode on the corresponding grid component
    if (this.gridComponents && grid.gridId) {
      const gridComponent = this.gridComponents.find(
        (gc) => gc.gridId() === grid.gridId,
      );
      if (gridComponent) {
        // Call toggleCompactMode with deselect: true and silent: true to clear the selection
        // without emitting rowClicked event (prevents loop)
        gridComponent.toggleCompactMode({}, undefined, true, true);
      }
    }

    // Update filter parameters signal
    this.#gridFilterParameters.update((params) => jsonDeepClone(params));

    this.#notifyGridChange();

    const t = setTimeout(() => {
      this.#siaParamsAndPatternsService.notifyHardParamsUpdated();
    });
    this.#timeouts.push(t);
  }

  processGridsHeight(): void {
    const currentGridConfig = this.siaMultiGrid();
    if (!currentGridConfig.grids) return;

    let containerSize: number =
      this.#siaSizeDetector.currentSize.contentHeight ?? 0;
    containerSize = containerSize - 32;

    const compactGrids =
      currentGridConfig.grids.filter((grid) => grid.compact).length ?? 0;
    const expandedGrids = currentGridConfig.grids.length - compactGrids;

    containerSize += -(68 * compactGrids); // 68 = 60px height multi grid close + 8px margin (padding 2)

    if (expandedGrids > 1) {
      containerSize = containerSize / expandedGrids;
    }
    this.expandedGridSize.set(containerSize);
  }

  private async loadConfiguration(): Promise<void> {
    const currentMultiGridId = this.multiGridId();
    const perspective =
      await this.#siaComponentConfigService.getDefaultPerspective(
        'multi-grid',
        currentMultiGridId,
        'MASTER',
      );

    if (!perspective) {
      this.siaMultiGrid.set({});
      return;
    }

    const config: SiaMultiGridConfiguration = JSON.parse(
      perspective.jsonConfig ?? '{}',
    );

    // Process grids asynchronously
    if (config.grids) {
      const gridPromises = config.grids.map(async (grid) => {
        if (grid.gridId) {
          grid.compact = (grid.hideUntilValueFor?.length ?? 0) > 0;
          grid.canLoadData = (grid.hideUntilValueFor ?? []).length === 0;
          const layout = await this.#siaLayoutService.getLayout(grid.gridId);
          if (layout) {
            grid.title = layout.title;
          }
        }
        return grid;
      });

      await Promise.all(gridPromises);

      // Update page data for each grid
      const currentPageData = this.pageData();
      config.grids.forEach((grid) => {
        grid.pageData = { ...currentPageData };
        grid.actionsPageData = { ...currentPageData, componentId: grid.gridId };
      });
    }

    this.siaMultiGrid.set(config);
    this.processGridsHeight();
  }

  ngOnDestroy(): void {
    // Clear all timeouts
    this.#timeouts.forEach((t) => {
      clearTimeout(t);
    });
    this.#timeouts = [];

    // Reset signals
    this.siaMultiGrid.set({});
    this.pageData.set(undefined);
    this.#gridFilterParameters.set({});
    this.expandedGridSize.set(0);
    this.multiGridId.set('');
  }

  ngOnInit(): void {
    const initialPageData = jsonDeepClone(
      this.#siaRouterService.getLatestRoutingData(),
    );
    this.pageData.set(initialPageData);
    this.loadConfiguration();
  }

  // Trigger change detection when grid configs mutate in-place
  #notifyGridChange(): void {
    this.siaMultiGrid.update((config) => ({
      ...config,
      grids: config.grids ? [...config.grids] : [],
    }));
  }

  selectedGridViewOutput($event: string, siaGrid: SiaMultiGrid): void {
    this.siaMultiGrid.update((multigrid) => {
      var grid = multigrid.grids?.find(
        (grid) => grid.gridId === siaGrid.gridId,
      );
      grid!.additionalTitle = $event;
      return { ...multigrid };
    });
  }
}
